import { BaseCreateComponent } from "../BaseCreateComponent";
import { renderFormHeader } from "../form/formHeader";
import { historyBackManager } from "../../services/historyBackManager";

/**
 * Base para modales a pantalla completa (`.aplic-form-overlay`).
 *
 * Provee:
 * - Header con back button + título + subtítulo
 * - Body scrollable (`.aplic-form-body`)
 * - Footer (`.aplic-form-footer`) con contenido definido por subclase
 * - `historyBackManager` para el botón atrás del dispositivo
 * - Animación de salida al hacer dispose
 *
 * Uso:
 * ```
 * class MiFormModal extends BaseModalComponent {
 *   protected renderBody(): string { return `<div>...</div>`; }
 *   protected renderFooter(): string { return `<button>Guardar</button>`; }
 * }
 * ```
 */
export abstract class BaseModalComponent extends BaseCreateComponent<HTMLDivElement> {
  // ── BaseCreateComponent ───────────────────────────────────
  protected getTagName(): string { return "div"; }
  protected getElementId(): string { return "baseModalOverlay"; }
  protected getCssClassList(): string[] { return ["aplic-form-overlay"]; }

  // ── Abstractos ────────────────────────────────────────────

  /** Título del header */
  protected abstract get title(): string;
  /** Subtítulo opcional del header */
  protected abstract get subtitle(): string | undefined;

  /** Retorna el HTML del body (dentro de `.aplic-form-body`) */
  protected abstract renderBody(): string;

  /**
   * Retorna el HTML del footer (dentro de `.aplic-form-footer`).
   * Retornar cadena vacía si el modal no necesita footer.
   */
  protected abstract renderFooter(): string;

  // ── Hooks opcionales ──────────────────────────────────────

  /** Cablear eventos después de renderizar */
  protected onAfterRender(_el: HTMLDivElement): void {}

  /** Personalizar configuración del header */
  protected getHeaderConfig(): { title?: string; subtitle?: string } {
    return { title: this.title, subtitle: this.subtitle };
  }

  /** Si false, el body se renderiza sin el wrapper `.aplic-form-body` */
  protected get wrapBody(): boolean {
    return true;
  }

  // ── Lifecycle ─────────────────────────────────────────────

  private _backHandler = () => this.dispose();

  protected override afterInit(el: HTMLDivElement): void {
    historyBackManager.push(this._backHandler);

    const footerHtml = this.renderFooter();
    const bodyHtml = this.renderBody();
    el.innerHTML = `
      ${renderFormHeader(this.getHeaderConfig())}
      ${this.wrapBody ? `<div class="aplic-form-body">${bodyHtml}</div>` : bodyHtml}
      ${footerHtml ? `<div class="aplic-form-footer">${footerHtml}</div>` : ""}
    `;

    el.querySelector<HTMLButtonElement>(".aplic-form-back-btn")!.onclick = () => this.dispose();
    this.onAfterRender(el);
  }

  public override dispose(): void {
    historyBackManager.remove(this._backHandler);
    const el = this.getElement();
    el.classList.add("exit");
    setTimeout(() => super.dispose(), 200);
  }
}
